/**
 * Star topology scene — Hub Campus.
 *
 * Campus layout:
 *   - Manager building (5x5) centered
 *   - Worker buildings (4x4) arranged around the manager
 *   - Stone paths connecting manager to each worker
 *   - Trees, benches, bushes scattered on grass
 *
 * Scales: workers placed in rows below/around the manager.
 */

import type { SceneLayout, TopologyInfo, BuildingFootprint, CampusDef } from "./SceneLayout.js";
import { campusToLayout, routePath } from "./SceneLayout.js";
import { getFurnitureForBuilding } from "./FurniturePresets.js";

const MGR_SIZE = 6;
const WORKER_SIZE = 4;
const GAP = 4; // cells between buildings

export function starLayout(topo: TopologyInfo): SceneLayout {
  const hub = topo.entryPoint ?? topo.agents[0]?.id;
  const workers = topo.agents.filter((a) => a.id !== hub);
  const count = workers.length;

  const buildings: BuildingFootprint[] = [];

  // --- Manager building centered ---
  const mgrCol = 0;
  const mgrRow = 0;

  buildings.push({
    id: "manager",
    col: mgrCol,
    row: mgrRow,
    width: MGR_SIZE,
    height: MGR_SIZE,
    floorType: "manager",
    agents: [{
      agentId: hub!,
      localCol: 3,
      localRow: 3,
      deskType: "large",
      isEntryPoint: true,
    }],
    furniture: getFurnitureForBuilding("entry", "corporate", mgrCol, mgrRow, MGR_SIZE, MGR_SIZE),
  });

  // --- Worker buildings arranged in rows below the manager ---
  const workersPerRow = Math.min(count, 3);
  const workerStartCol = mgrCol - Math.floor(((workersPerRow - 1) * (WORKER_SIZE + GAP)) / 2);
  const workerStartRow = mgrRow + MGR_SIZE + GAP;

  for (let i = 0; i < count; i++) {
    const gridR = Math.floor(i / workersPerRow);
    const gridC = i % workersPerRow;
    const bCol = workerStartCol + gridC * (WORKER_SIZE + GAP);
    const bRow = workerStartRow + gridR * (WORKER_SIZE + GAP);

    buildings.push({
      id: `worker-${i}`,
      col: bCol,
      row: bRow,
      width: WORKER_SIZE,
      height: WORKER_SIZE,
      floorType: "office",
      agents: [{
        agentId: workers[i].id,
        localCol: 2,
        localRow: 2,
        deskType: "small",
        isEntryPoint: false,
      }],
      furniture: getFurnitureForBuilding("worker", "corporate", bCol, bRow, WORKER_SIZE, WORKER_SIZE),
    });
  }

  // --- Route paths from manager to each worker ---
  const pathCells: Array<{ col: number; row: number }> = [];
  const mgrBuilding = buildings[0];
  for (let i = 1; i < buildings.length; i++) {
    pathCells.push(...routePath(mgrBuilding, buildings[i]));
  }

  const campus: CampusDef = {
    buildings,
    pathCells,
    decorations: [],
  };

  return campusToLayout(campus);
}
